import React, { useState } from "react";
import { Instagram, Facebook, MessageCircle, Loader2, ArrowRight } from "lucide-react";
import { subscribeNewsletter } from "@/lib/api";
import { toast } from "sonner";
import { CONTACT, FSSAI, LOCATIONS } from "@/data/cafe";
import { scrollToId } from "@/lib/scroll";

const cols = [
  { title: "Explore", links: [["About", "#about"], ["Menu", "#menu"], ["Gallery", "#gallery"], ["Franchise", "#franchise"], ["Contact", "#contact"]] },
  { title: "Legal", links: [["Privacy Policy", "#"], ["Terms", "#"], ["Loyalty Rewards", "#"], ["Gift Cards", "#"]] },
];

export default function Footer() {
  const [loading, setLoading] = useState(false);
  const subscribe = async (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    setLoading(true);
    try {
      const res = await subscribeNewsletter(email);
      toast.success(res.message);
      if (!res.already) e.target.reset();
    } catch { toast.error("Please enter a valid email."); }
    finally { setLoading(false); }
  };
  const go = (href) => href.startsWith("#") && scrollToId(href);

  return (
    <footer className="bg-[#1A1514] text-[#F5F2EB] pt-20 pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pb-14 border-b border-white/10">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Teaavi Coffee" className="h-12 w-12 rounded-full object-cover" />
              <span className="font-display text-3xl">Teaavi Coffee</span>
            </div>
            <p className="font-display italic text-2xl text-[#F5F2EB]/70 mt-4">Brewed with Passion. Served with Heart.</p>

            <form onSubmit={subscribe} className="mt-8 max-w-sm" data-testid="newsletter-form">
              <p className="overline text-[#1C7C54]">Newsletter</p>
              <div className="flex mt-3 border border-white/20 rounded-sm overflow-hidden focus-within:border-[#1C7C54] transition-colors">
                <input name="email" type="email" required placeholder="your@email.com" data-testid="newsletter-input"
                  className="flex-1 bg-transparent px-4 py-3 font-body text-sm outline-none placeholder:text-[#F5F2EB]/40" />
                <button type="submit" disabled={loading} data-testid="newsletter-submit"
                  className="px-4 bg-[#1C7C54] text-[#F5F2EB] hover:bg-[#14603F] transition-colors grid place-items-center">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                </button>
              </div>
            </form>
          </div>

          {cols.map((c) => (
            <div key={c.title} className="lg:col-span-2">
              <p className="overline text-[#F5F2EB]/50">{c.title}</p>
              <ul className="mt-5 space-y-3">
                {c.links.map(([label, href]) => (
                  <li key={label}>
                    <button onClick={() => go(href)} className="font-body text-[#F5F2EB]/70 hover:text-[#1C7C54] transition-colors">{label}</button>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-3">
            <p className="overline text-[#F5F2EB]/50">Visit — Our Cafés</p>
            <ul className="mt-5 space-y-4" data-testid="footer-addresses">
              {LOCATIONS.map((l) => (
                <li key={l.name}>
                  <a href={l.maps} target="_blank" rel="noreferrer" className="group block" data-testid={`footer-address-${l.name}`}>
                    <p className="font-accent text-xs uppercase tracking-[0.12em] text-[#1C7C54]">{l.name.replace("Teaavi Coffee — ", "")}</p>
                    <p className="font-body text-sm text-[#F5F2EB]/60 leading-relaxed group-hover:text-[#F5F2EB] transition-colors">{l.address}</p>
                  </a>
                </li>
              ))}
            </ul>
            <div className="flex gap-3 mt-6">
              {[[Instagram, CONTACT.socials.instagram], [Facebook, CONTACT.socials.facebook], [MessageCircle, CONTACT.socials.whatsapp]].map(([Icon, href], i) => (
                <a key={i} href={href} target="_blank" rel="noreferrer" data-testid={`social-${i}`}
                  className="h-10 w-10 grid place-items-center border border-white/20 rounded-sm hover:bg-[#1C7C54] hover:border-[#1C7C54] transition-colors">
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-8">
          <p className="font-body text-sm text-[#F5F2EB]/40">© {new Date().getFullYear()} Teaavi Coffee. All rights reserved.</p>
          <p className="font-body text-sm text-[#F5F2EB]/40" data-testid="fssai-license">FSSAI Lic. No. {FSSAI}</p>
        </div>
      </div>
    </footer>
  );
}
